import { useEffect, useState } from "react"
import { useParams } from "react-router"
import type { ServerFileEntry } from "@mc/types"
import { listServerFiles, readServerFile, writeServerFile, deleteServerFile } from "../api/files.js"
import { friendlyError } from "../api/errorMessages.js"
import { Spinner } from "../components/ui/Spinner.js"
import { Button } from "../components/ui/Button.js"
import { useToast } from "../components/ui/Toast.js"
import { Coachmark } from "../components/onboarding/Coachmark.js"
import { useFirstVisit } from "../components/onboarding/useFirstVisit.js"
import { getHint } from "../components/onboarding/hints.js"

type BrowseState =
  | { status: "loading" }
  | { status: "ready"; entries: ServerFileEntry[] }
  | { status: "error"; message: string }

type EditorState =
  | { status: "closed" }
  | { status: "loading"; path: string }
  | { status: "open"; path: string; content: string; original: string }
  | { status: "error"; path: string; message: string }

/**
 * `/servers/[id]/files` (FR seccion 19 item 8). Editor de texto plano
 * (`<textarea>`), no Monaco: se prefirio cerrar el resto de Fase 12
 * primero (Config/Mods/Jugadores reusan este mismo backend) en vez de
 * sumar la dependencia y la integracion de Monaco en la misma sesion.
 * El backend (listar/leer/escribir/borrar por path) ya esta completo,
 * asi que cambiar el editor a Monaco despues es un cambio acotado al
 * componente, no al resto del sistema.
 */
export function ServerFilesPage() {
  const { serverId } = useParams<{ serverId: string }>()
  const [path, setPath] = useState("")
  const [browse, setBrowse] = useState<BrowseState>({ status: "loading" })
  const [editor, setEditor] = useState<EditorState>({ status: "closed" })
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const [isFirstVisit, dismissFirstVisit] = useFirstVisit("server-files")

  useEffect(() => {
    if (!serverId) return
    load(serverId, path)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId, path])

  function load(id: string, currentPath: string) {
    setBrowse({ status: "loading" })
    listServerFiles(id, currentPath)
      .then(({ entries }) => setBrowse({ status: "ready", entries }))
      .catch((err) => setBrowse({ status: "error", message: friendlyError(err).message }))
  }

  function openFile(entry: ServerFileEntry) {
    if (!serverId) return
    setEditor({ status: "loading", path: entry.path })
    readServerFile(serverId, entry.path)
      .then(({ content }) => setEditor({ status: "open", path: entry.path, content: content ?? "", original: content ?? "" }))
      .catch((err) => setEditor({ status: "error", path: entry.path, message: friendlyError(err).message }))
  }

  async function handleSave() {
    if (!serverId || editor.status !== "open") return
    setSaving(true)
    try {
      await writeServerFile(serverId, editor.path, editor.content)
      setEditor((prev) => (prev.status === "open" ? { ...prev, original: prev.content } : prev))
      toast.show("Archivo guardado.", "success")
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setSaving(false)
    }
  }

  function handleDelete(entry: ServerFileEntry) {
    toast.confirmDestructive(
      `Se va a eliminar "${entry.name}".`,
      async () => {
        if (!serverId) return
        try {
          await deleteServerFile(serverId, entry.path)
          if (editor.status === "open" && editor.path === entry.path) setEditor({ status: "closed" })
          load(serverId, path)
          toast.show("Eliminado.", "success")
        } catch (err) {
          toast.show(friendlyError(err).message, "error")
        }
      },
      () => toast.show("Eliminación cancelada.", "info"),
    )
  }

  const breadcrumbs = ["", ...path.split("/").filter(Boolean)]

  const browser =
    browse.status === "loading" ? (
      <div style={{ display: "grid", placeItems: "center", padding: "var(--space-8) 0" }}>
        <Spinner size="md" />
      </div>
    ) : browse.status === "error" ? (
      <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
        {browse.message}
      </p>
    ) : browse.entries.length === 0 ? (
      <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>Carpeta vacía.</p>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        {browse.entries.map((entry) => (
          <div
            key={entry.path}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--space-3)",
              padding: "var(--space-3)",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-default)",
              background: editor.status !== "closed" && "path" in editor && editor.path === entry.path ? "var(--brand-subtle)" : "var(--bg-surface)",
              cursor: entry.type === "directory" ? "pointer" : "default",
              transition: "background-color var(--duration-small) var(--ease-out)",
            }}
            onClick={() => {
              if (entry.type === "directory") setPath(entry.path)
            }}
          >
            <span style={{ fontSize: "var(--text-sm)", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
              {entry.type === "directory" ? "📁" : "📄"} {entry.name}
            </span>
            <div style={{ display: "flex", gap: "var(--space-2)" }} onClick={(e) => e.stopPropagation()}>
              {entry.type === "file" && (
                <Button size="sm" variant="secondary" onClick={() => openFile(entry)}>
                  Editar
                </Button>
              )}
              <Button size="sm" variant="destructive" onClick={() => handleDelete(entry)}>
                Borrar
              </Button>
            </div>
          </div>
        ))}
      </div>
    )

  const content = (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div style={{ display: "flex", gap: "var(--space-1)", fontSize: "var(--text-sm)", flexWrap: "wrap" }}>
        {breadcrumbs.map((segment, i) => {
          const segPath = breadcrumbs.slice(1, i + 1).join("/")
          return (
            <span key={i}>
              {i > 0 && <span style={{ color: "var(--text-muted)" }}> / </span>}
              <button
                onClick={() => setPath(segPath)}
                style={{ background: "none", border: "none", color: "var(--brand-primary)", cursor: "pointer", padding: 0 }}
              >
                {segment === "" ? "raíz" : segment}
              </button>
            </span>
          )
        })}
      </div>

      {browser}

      {editor.status === "loading" && (
        <div style={{ display: "grid", placeItems: "center", padding: "var(--space-8) 0" }}>
          <Spinner size="md" />
        </div>
      )}

      {editor.status === "error" && (
        <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
          {editor.message}
        </p>
      )}

      {editor.status === "open" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong style={{ fontSize: "var(--text-sm)" }}>{editor.path}</strong>
            <Button size="sm" variant="ghost" onClick={() => setEditor({ status: "closed" })}>
              Cerrar
            </Button>
          </div>
          <textarea
            value={editor.content}
            onChange={(e) => setEditor((prev) => (prev.status === "open" ? { ...prev, content: e.target.value } : prev))}
            spellCheck={false}
            style={{
              fontFamily: "var(--font-mono, ui-monospace, monospace)",
              fontSize: "var(--text-sm)",
              lineHeight: 1.6,
              background: "var(--bg-primary)",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-md)",
              padding: "var(--space-4)",
              minHeight: "40vh",
              color: "var(--text-primary)",
              resize: "vertical",
            }}
          />
          <Button loading={saving} disabled={editor.content === editor.original} onClick={handleSave} style={{ alignSelf: "flex-start" }}>
            Guardar
          </Button>
        </div>
      )}
    </div>
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <h1 style={{ fontSize: "var(--text-3xl)" }}>Archivos</h1>
        <p style={{ color: "var(--text-muted)" }}>Explorador de archivos del servidor.</p>
      </div>
      {isFirstVisit ? (
        <Coachmark title={getHint("server-files").title} description={getHint("server-files").description} onDismiss={dismissFirstVisit}>
          {content}
        </Coachmark>
      ) : (
        content
      )}
    </div>
  )
}
